/**
 * Close & submit — ad hoc items, the gap summary, and the Submit button
 * (spec §2.5, mockup screen 6).
 *
 * ── What Submit does, in this order ──────────────────────────────────────────
 *   1. runs the engines           — `detectGaps()` + `validate()` via `planFrom`
 *   2. sets the status            — `surveyStatusFor()`: submitted_clean or
 *                                   submitted_with_gaps, never a guess
 *   3. PERSISTS LOCALLY           — one IndexedDB transaction: status, gaps,
 *                                   submittedAt, and the queued document push
 *   4. queues the submission      — an outbox row; the sync engine delivers it
 *                                   and the server fires `survey_submitted`
 *
 * Steps 3 and 4 both complete with the radio off. A survey submitted in a pit is
 * a submitted survey on the device the instant the thumb lifts; the office finds
 * out when there is signal, and the button says exactly that.
 *
 * ── The surveyor cannot clear a gap (§2.3) ───────────────────────────────────
 * There is no dismiss, no "resolve", no override anywhere on this screen. Gaps
 * are listed with the section they came from and the reason, and they go with
 * the record. Only Chip and Tim decide: return trip, or estimate around it.
 *
 * ── THE ONE GATE, and what it is NOT (2026-08-06) ────────────────────────────
 * The design authority found two defects here:
 *
 *   "There is a 'Close and Submit' button which could easily be clicked by
 *    mistake. Users should not be able to close and submit until all sections
 *    are complete."
 *   "The survey form currently allows users to close and submit before the
 *    second machine room has been surveyed."
 *
 * So Submit is now PROGRESSIVELY DISCLOSED: until `guidePlan().ready`, this
 * screen shows what is left and the one next action, and the Submit button does
 * not exist — not greyed out, absent, because a dead control is a puzzle.
 *
 * The gate is "every machine room has been WALKED", nothing more. It is not a
 * completeness check and it must never become one:
 *
 *   - a locked-out pit still submits (§2.3) — flagging one IS walking it
 *   - a missing recommended photo still never blocks (§4 rule 8)
 *   - an unanswered question still never blocks; it travels as a gap
 *
 * Nothing below removes, adds, or re-decides a gap. `detectGaps` and `validate`
 * still produce exactly what they produced before, and every one of them still
 * goes with the record.
 *
 * ── THE SECOND GATE — the last look (D-017, 2026-08-06) ──────────────────────
 *
 *   "I just closed and submitted an incomplete survey… I should not have been
 *    able to submit with unanswered questions."
 *
 * Once every room is walked, this screen shows what the survey never got an
 * answer for — grouped by section, in plain language — and Submit appears only
 * once each one has been ANSWERED, in place, on that screen (D-023, and D-038:
 * answering is the only door). See `src/ui/state/reckoning.ts` for what
 * qualifies and, more importantly, for what deliberately does not: a survey
 * with a genuine gap still submits (§2.3), and a missing photo still never
 * blocks (§4 rule 8). The distinction being enforced is deliberate versus
 * accidental, not complete versus incomplete — a question nobody could get is
 * answered No / 0 with the blockage in that section's notes (D-033 R15), and it
 * travels to the office as the gaps it always did.
 *
 * ── A submitted survey is still reachable (feedback c) ───────────────────────
 * "Once 'Close and Submit' is pressed, the survey becomes inaccessible, and
 * there is no clear way to return to it." It is not inaccessible: it sits on
 * Home under Submitted, and this sealed face — what was sent, every gap that
 * travelled with it — is still one tap away, off the map's own "Review &
 * Submit" row (controller ruling 13, round 7 task 10). Sealed server-side,
 * and that does not change — there is no edit control on this screen and
 * there must never be one.
 *
 * ── Home opens Building now, not this screen (round 7 task 10) ──────────────
 * Stephanie: *"When the user is on the home screen and clicks on one of their
 * submitted surveys, it should open the Building Config screen in read-only
 * mode."* The Home row (`SwipeableRow.tsx`) points at `{ name: 'building' }`
 * unconditionally now — `useSurvey().readOnly` is what makes that screen, and
 * every screen reachable from it, read-only. This screen is unchanged in
 * every other respect; the map is simply a second door onto it.
 */

import { useState, type ReactNode } from 'react';

import {
  AdHocList,
  Card,
  Eyebrow,
  GapBox,
  LastLook,
} from '../components';
import { isoAt } from '../../db/store';
import { hrefFor } from '../routing';
import { navigate } from '../useRoute';
import { nextRoomId } from '../state/buildingSetup';
import {
  guidePlan,
  notReadySummary,
  submittedFacts,
} from '../state/guide';
import { buildReckoning } from '../state/reckoning';
import { useServices } from '../state/services';
import { applySubmission, planFrom, sealedLine, submitConsequence } from '../state/submit';
import { isSubmitted, summarize } from '../state/surveyList';
import { useSurvey } from '../state/SurveyProvider';
import { useWalkedSections } from '../state/walked';

export interface CloseScreenProps {
  surveyId: string;
}

export function CloseScreen({ surveyId }: CloseScreenProps): ReactNode {
  const { store, sync, identity } = useServices();
  const { record, doc, results, setStructure } = useSurvey();
  const walked = useWalkedSections(surveyId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * D-051: the last look does not close under a finger. When the last open
   * item is answered, `reckoning.settled` flips on the first keystroke and
   * this screen would swap the whole list for the ready face — the field the
   * surveyor is typing into vanishes with one character in it (VA Tuskegee,
   * ZIP "3"). So while anything inside the last look has focus, the list
   * stays; the swap happens on blur, which is the tap that leaves the field.
   */
  const [holding, setHolding] = useState(false);


  const plan = planFrom(results);
  const guide = guidePlan(results, walked);
  const reckoning = buildReckoning(results, surveyId);
  const submitted = isSubmitted(doc.status);

  const submit = async (): Promise<void> => {
    setBusy(true);
    try {
      // One write, durably: `setStructure` is `store.updateDocument`, which
      // commits the sealed document AND queues the submission in the same
      // transaction (D-049). There is no second step for a locked phone to
      // cut off. Offline the row simply sits in the outbox; the engine
      // drains it when connectivity returns.
      await setStructure((document) => {
        applySubmission(document, plan, isoAt(store.now()), identity?.name);
      });
      void sync.drain().catch(() => undefined);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not submit on this device.');
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    const facts = submittedFacts(results);
    /*
     * THE SEALED FACE, de-noised (round 7, task 9). The design authority:
     * "It should instead display the surveyor's name and the submission
     * date," and "Remove the save-backup button… along with the helper text
     * at the bottom… De-noise this screen as well. Remove the My Surveys
     * button. If the Surveyor wants to go back to the home screen, they can
     * tap the Upsurv logo at the upper, top left."
     *
     * So: `sealedLine` (submit.ts) replaces `submittedSummary` — who and
     * when, off the record itself, never a story about what happens next.
     * The facts line drops "· sent {day}" — the same date, said once, is
     * `sealedLine`'s job now. `SaveSurveyCopy`, `RemoveSurvey` (and the
     * blocked-office explanation it alone rendered) and the "My surveys"
     * anchor are gone; the app's own logo, present on every screen, is the
     * way back she named.
     */
    return (
      <>
        <Eyebrow>Submitted</Eyebrow>
        <div className="card" data-testid="submitted-record">
          <p className="q" data-legibility="question">{sealedLine(doc)}</p>
        </div>

        {/* Feedback (c): "the survey becomes inaccessible, and there is no clear
         * way to return to it." It is right here, and it says what went. Read
         * only — the office's copy is sealed and this one matches it. */}
        <Eyebrow>What was sent</Eyebrow>
        <div className="card">
          <p className="q" data-legibility="question">{summarize(record).title}</p>
          <p className="ruleline" data-legibility="helper">
            {plural(facts.rooms, 'machine room')} · {plural(facts.sections, 'section')} ·{' '}
            {plural(facts.photos, 'photo')}
          </p>
        </div>

        {doc.gaps.length > 0 ? <Eyebrow>What went with it</Eyebrow> : null}
        {doc.gaps.map((gap) => (
          <GapRow
            key={gap.id}
            sectionTitle={gap.sectionTitle}
            message={gap.message}
            flagged={gap.reason === 'surveyor_flagged'}
            {...(gap.surveyorNote ? { note: gap.surveyorNote } : {})}
          />
        ))}
      </>
    );
  }

  /*
   * NOT READY — the wizard's screen, not an error screen.
   *
   * No red, no alarm word, no count of everything wrong: the surveyor has not
   * made a mistake, there is simply more job. It names what is left, offers the
   * ONE next action, and does not render a Submit button at all.
   */
  if (!guide.ready) {
    return (
      <>
        <Eyebrow>Not finished yet</Eyebrow>
        <Card>
          <p className="q" data-legibility="question">{notReadySummary(guide)}</p>
        </Card>

        {/* What is left lives on the MAP — one list, one place (D-029 R7).
          * This face says the gate's sentence and offers the one next action. */}
        <p className="wizard" data-legibility="helper">
          <span className="wizard__next">{guide.step.sentence}</span>
        </p>

        <div className="walk">
          {guide.step.kind === 'addRoom' ? (
            /* The button opens the first free SLOT (`nextRoomId`, D-037) — the
             * same Room Setup the map's own pending row for it opens. The room
             * itself is still born on that screen, by the M1 tap. */
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => navigate({ name: 'roomSetup', surveyId, roomId: nextRoomId(doc) })}
            >
              {guide.step.action}
            </button>
          ) : (
            <a
              className="btn btn--primary"
              href={hrefFor(guide.step.route)}
              aria-label={guide.step.action}
            >
              {guide.step.action}
            </a>
          )}
        </div>
      </>
    );
  }

  /*
   * THE LAST LOOK — D-017, and the second half of the gate.
   *
   * The walk is finished; what is left is answers the survey never got. They
   * are shown, grouped by section, with the real control on each row, and every
   * one has to be ANSWERED before Submit is offered (D-038). Submit is ABSENT
   * until then, for the same reason it is absent above: a dead control is a
   * puzzle.
   *
   * This is not a completeness gate and must never become one. Every §2.3
   * escape stays open — a question nobody could get is answered No / 0 with the
   * blockage written in that section's notes, and a missing recommended photo is
   * still §4 rule 8's business, listed below and never blocking. What is closed
   * is the one case the design authority actually hit: a question nobody ever
   * saw, going to the office silently.
   */
  if (!reckoning.settled || holding) {
    return (
      <div
        className="lastlook-hold"
        onFocusCapture={() => setHolding(true)}
        onBlurCapture={() => setHolding(false)}
      >
        <LastLook reckoning={reckoning} />
      </div>
    );
  }

  return (
    <>
      {/*
       * The SAME checklist the walk's Ad Hoc Items section shows, writing the
       * same `doc.adHoc` through the same component (D-023). It is one list seen
       * twice — at the end of the walk and again here on the way out — never two
       * copies that could disagree, and the eyebrow is now the one string both
       * surfaces print: the walk's own section title (task 7).
       */}
      <Eyebrow>Ad Hoc Items</Eyebrow>
      <AdHocList />

      {/* The amber sentence about recommended photos not taken stood here
       * (Amendment 5). The design authority, 2026-09-01, named it an example
       * of noise (D-033 R16). They still travel as gaps —
       * `plan.recommendedPhotoGaps` is unchanged. */}

      {/* The count is in the heading as well as the button: on a survey that is
       * barely started this list is long, and "3 of 101" is the difference
       * between reading it and scrolling past it. */}
      {plan.blocking.length > 0 ? (
        <Eyebrow>Gaps travelling with this survey · {plan.blocking.length}</Eyebrow>
      ) : null}
      {plan.blocking.map((gap) => {
        /*
         * WHERE to go about it (2026-08-18). A gap on this list names its section
         * in words; the section title is now the way there too. Chip read
         * *"Machine Room 1 · Room Setup — Car 4 isn't in a hoistway yet."* here
         * with nothing to tap. The last look's link to the section, and this one, left with D-033 (R7).
         * The ad hoc checklist is on this very screen, so it gets no link.
         */
        return (
          <GapRow
            key={gap.id}
            sectionTitle={gap.sectionTitle}
            message={gap.message}
            flagged={gap.reason === 'surveyor_flagged'}
            {...(gap.surveyorNote ? { note: gap.surveyorNote } : {})}
          />
        );
      })}

      {/* No gaps says nothing (R11), and who owns a gap is not said here any
       * more (R1) — the rows above are the state. */}

      <button
        type="button"
        className="btn btn--primary btn--submit-survey"
        disabled={busy}
        onClick={() => void submit()}
      >
        {busy ? 'Submitting…' : 'Submit Survey'}
        <small>{busy ? 'Saving to this phone…' : submitConsequence(plan)}</small>
      </button>

      {error ? (
        <p className="photos__error" role="alert">
          {error}
        </p>
      ) : null}

    </>
  );
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

/**
 * One gap. A surveyor-flagged one already names its section inside its own
 * message — it was written to travel — so it is not labelled twice. The
 * standing sentence about who decides, and the link to the section, left with
 * D-033 (R1, R7): the map is the way to a section, and the office's role is
 * not restated under every gap.
 */
function GapRow({
  sectionTitle,
  message,
  flagged,
  note,
}: {
  sectionTitle: string;
  message: string;
  flagged: boolean;
  note?: string;
}): ReactNode {
  if (flagged) {
    return (
      <GapBox>
        <b>{message}</b>
      </GapBox>
    );
  }
  return (
    <GapBox>
      <b>{sectionTitle}</b>
      {' — '}
      {message}
      {note ? ` (${note})` : ''}
    </GapBox>
  );
}
